import _ from 'lodash'
import ContractFormHelper from '../components/editor/form_builder/FormHelper'
import affirm from '../core/Affirm'
import { IClassifiedFeature } from '../definitions/Classification'
import { IContract } from '../definitions/Contract'
import { IContractSchema } from '../definitions/ContractSchema'
import { IFormField } from '../definitions/FormField'
import { IFormGroup } from '../definitions/FormGroup'
import { ISystemConfig } from '../definitions/MiscSchema'
import Helper from '../helpers/Helper'
import editorOptionsRegistry from './EditorOptionsRegistry'
import { ISystemConfigsCollection } from '../definitions/Common'

class ContractIngestionManagerClass {
    private steps: IFormGroup[] = []
    private allFields: IFormField[] = []

    runIngestionManager = async (features: IClassifiedFeature[], contract: IContract, systemConfigsCollection: ISystemConfigsCollection[], contractSchema: IContractSchema): Promise<{ contract: IContract, features: IClassifiedFeature[] }> => {
        affirm(systemConfigsCollection, 'systemConfigsCollection is required')
        affirm(contract, 'contract is required')
        affirm(features, 'features from AI is required')
        affirm(contractSchema, 'contractSchema is required')

        this.steps = contractSchema.steps
        this.allFields = this.steps.flatMap(step => ContractFormHelper.flatSteps(step))
        editorOptionsRegistry.setOptions(systemConfigsCollection)

        const result = this.populateContractWithFeatures(features, contract)
        return { contract: result.contract, features: result.featuresApplied }
    }

    private getFieldByPath = (path: string): IFormField | undefined => {
        return this.allFields.find(f => f.key === path)
    }

    private hasNestedFields = (basePath: string): boolean => {
        return this.allFields.some(f => f.key.startsWith(`${basePath}.`))
    }

    private isValidValueForField = (value: any, field: IFormField | undefined, path: string): boolean => {
        if (!field && this.hasNestedFields(path)) {
            return typeof value === 'object' && value !== null && !Array.isArray(value)
        }

        if (!field) return true

        const fieldType = field.type

        switch (fieldType) {
            case 'object':
                return typeof value === 'object' && value !== null && !Array.isArray(value)
            case 'list':
                return Array.isArray(value)
            case 'number':
                return typeof value === 'number' || !isNaN(parseFloat(String(value)))
            default:
                return true
        }
    }

    populateContractWithFeatures = (features: IClassifiedFeature[], contract: IContract): { contract: IContract, featuresApplied: IClassifiedFeature[] } => {
        const updatedContract = _.cloneDeep(contract)
        const featuresApplied: IClassifiedFeature[] = []

        features.forEach(({ name, value, confidence, source }) => {
            if (!name || value === undefined || value === null || value === '') return

            const field = this.getFieldByPath(name)

            if (!this.isValidValueForField(value, field, name)) return

            const fieldType = field?.type
            let applied = false

            switch (fieldType) {
                case 'list': {
                    this.handleListFeature(name, value, updatedContract, featuresApplied, confidence, source, features, field)
                    applied = true
                    break
                }

                case 'system-config':{
                    const resolved = editorOptionsRegistry.resolveValue(field, value)
                    if (resolved !== null) {
                        _.set(updatedContract, name, resolved)
                        applied = true
                    }
                    break
                }
                case 'string-options': {
                    const resolved = editorOptionsRegistry.resolveValue(field, value)
                    if (resolved !== null) {
                        _.set(updatedContract, name, resolved.name)
                        applied = true
                    }
                    break
                }

                case 'multi-system-config': {
                    if (Array.isArray(value)) {
                        const resolvedArray = value.map(v => editorOptionsRegistry.resolveValue(field, v)).filter((x): x is ISystemConfig => x !== null)
                        if (resolvedArray.length > 0) {
                            _.set(updatedContract, name, resolvedArray)
                            applied = true
                        }
                    }
                    break
                }

                case 'number': {
                    const num = parseFloat(String(value).replace(/[^\d.-]/g, ''))
                    _.set(updatedContract, name, isNaN(num) ? null : num)
                    applied = true
                    break
                }

                case 'date': {
                    const safeDate = Helper.parseSafeDate(value)
                    if (safeDate) {
                        _.set(updatedContract, name, safeDate)
                        applied = true
                    }
                    break
                }

                default: {
                    _.set(updatedContract, name, value)
                    applied = true
                    break
                }
            }

            if (applied) {
                featuresApplied.push({ name, value, confidence, source, assigned: true })
            }
        })

        return { contract: updatedContract, featuresApplied }
    }

    handleListFeature = (name: string, value: any, contract: IContract, featuresApplied: IClassifiedFeature[], confidence: number, source: string, allFeatures: IClassifiedFeature[], field?: IFormField) => {
        const rootListName = name.replace(/\[\d+\]$/, '')
        if (name !== rootListName && allFeatures.some(f => f.name === rootListName)) return

        let arr: any[] = _.get(contract, name)

        if (Array.isArray(arr) && arr.length > 0) return

        if (!Array.isArray(arr)) {
            arr = []
            _.set(contract, name, arr)
        }

        const items = Array.isArray(value) ? value : value == null ? [] : [value]

        if (items.length === 0) return

        items.forEach((item: any, index: number) => {
            if (!item || typeof item !== 'object') return
            const validatedItem = this.validateAndCleanObject(item, field)

            if (!validatedItem || Object.keys(validatedItem).length === 0) return

            const itemWithUuid = { uuid: Helper.generateUUID(), ...validatedItem }
            arr.push(itemWithUuid)

            const basePath = `${name}[${index}]`
            featuresApplied.push({
                name: basePath,
                value: itemWithUuid,
                confidence,
                source,
                assigned: true
            })

            this.markValidatedNestedFields(validatedItem, basePath, featuresApplied, confidence, source)
        })
    }

    private validateAndCleanObject = (obj: any, parentField?: IFormField): any => {
        if (obj == null || typeof obj !== 'object' || Array.isArray(obj)) return obj

        const cleaned: any = {}

        Object.entries(obj).forEach(([key, value]) => {
            const fieldPath = parentField ? `${parentField.key}.${key}` : key
            const field = this.getFieldByPath(fieldPath)

            if (!field) {
                cleaned[key] = value
                return
            }

            const validatedValue = this.validateFieldValue(value, field)

            if (validatedValue !== null && validatedValue !== undefined) {
                cleaned[key] = validatedValue
            }
        })

        return cleaned
    }

    private validateFieldValue = (value: any, field: IFormField): any => {
        if (value === undefined || value === null || value === '') {
            return null
        }

        const fieldType = field.type

        switch (fieldType) {
            case 'system-config': {
                const resolved = editorOptionsRegistry.resolveValue(field, value)
                return resolved !== null ? resolved : null
            }

            case 'string-options': {
                const resolved = editorOptionsRegistry.resolveValue(field, value)
                return resolved !== null ? resolved.name : null
            }

            case 'multi-system-config': {
                if (Array.isArray(value)) {
                    const resolvedArray = value
                        .map(v => editorOptionsRegistry.resolveValue(field, v))
                        .filter((x): x is ISystemConfig => x !== null)
                    return resolvedArray.length > 0 ? resolvedArray : null
                }
                return null
            }

            case 'number': {
                const num = parseFloat(String(value).replace(/[^\d.-]/g, ''))
                return isNaN(num) ? null : num
            }

            case 'date': {
                const safeDate = Helper.parseSafeDate(value)
                return safeDate ?? null
            }

            case 'list': {
                if (!Array.isArray(value)) {
                    return null
                }
                return value
                    .map(item => this.validateAndCleanObject(item, field))
                    .filter(item => item !== null && (typeof item !== 'object' || Object.keys(item).length > 0))
            }

            case 'object': {
                if (typeof value !== 'object' || Array.isArray(value)) {
                    return null
                }
                return this.validateAndCleanObject(value, field)
            }

            default: {
                return value
            }
        }
    }

    private markValidatedNestedFields = (obj: any, path: string, featuresApplied: IClassifiedFeature[], confidence: number, source: string) => {
        if (obj == null || typeof obj !== 'object') {
            return
        }

        if (Array.isArray(obj)) {
            obj.forEach((e, i) => this.markValidatedNestedFields(e, `${path}[${i}]`, featuresApplied, confidence, source))
        } else {
            Object.entries(obj).forEach(([k, v]) => {
                if (k === 'uuid') return

                const fieldPath = `${path}.${k}`
                featuresApplied.push({
                    name: fieldPath,
                    value: v,
                    confidence,
                    source,
                    assigned: true
                })

                if (v != null && typeof v === 'object') {
                    this.markValidatedNestedFields(v, fieldPath, featuresApplied, confidence, source)
                }
            })
        }
    }
}

const contractIngestionManager = new ContractIngestionManagerClass()
export default contractIngestionManager
